import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import {
  ICliente,
  IDispositivoAlarma,
  IEventoGenerico,
  IFilter,
  ILayerMapaPersonalizado,
  ILuminaria,
  IPopulate,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  ISirena as ISirenaG,
  IUsuario,
} from 'modelos/src';
import { ISirena } from 'modelosSeguridad/src';
import { Subscription } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { LoginService } from '../../../login/login.service';
import { WebSocketService } from '../../../../auxiliares/servicios/websocket.service';
import {
  FILTRO_EVENTOS_TECNICOS,
  ReconciliacionWs,
} from '../../../../auxiliares/servicios/ws-reconciliacion';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { MatDialog } from '@angular/material/dialog';
import { TratamientoGenericoComponent } from '../../eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component';
import {
  CrearEditarEventoTecnicoGenericoComponent,
  ICrearEditarEventoTecnicoGenericoData,
} from '../../eventos-genericos/eventos-tecnicos-genericos/crear-editar/crear-editar-evento-tecnico-generico.component';
import { AsignarGenericoComponent } from '../../administracion/eventos-tecnicos-genericos/asignar/asignar.component';
import { FlotanteServicioTecnicoComponent } from './flotante-servicio-tecnico/flotante-servicio-tecnico.component';
import { VehicleCompositeIconService } from '../../../../auxiliares/mapbox/services/vehicle-composite-icon.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

export interface IInfoPanel {
  tipo: 'alarma' | 'luminaria' | 'sirena' | 'vehiculo';
  nombre: string;
  cliente?: string;
  direccion?: string;
  marcaModelo?: string;
  patente?: string;
  eventosTecnicos: (IEventoGenerico & { _tecnicoNombre?: string })[];
}

@Component({
  selector: 'app-mapa-servicio-tecnico',
  templateUrl: './mapa-servicio-tecnico.component.html',
  styleUrl: './mapa-servicio-tecnico.component.scss',
  standalone: false,
})
export class MapaServicioTecnicoComponent implements OnInit, OnDestroy {
  private lista2Service = inject(Lista2Service);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private vehicleCompositeIconService = inject(VehicleCompositeIconService);
  private cdr = inject(ChangeDetectorRef);
  private wsService = inject(WebSocketService);
  private helper = inject(HelperService);

  // Updates de eventogenerico (los ST del mapa) → re-listar con debounce.
  // El polling de 120s queda como reconciliación. El mensaje es solo un
  // trigger, así que va en modo 'trigger' y filtrado server-side por
  // filtrador: sin el filtro entraría todo el room (decenas de msg/s).
  private reconciliacionWs = new ReconciliacionWs(this.wsService, {
    entidades: ['eventogenerico'],
    idPermiso: () => LoginService.getIdPermiso(),
    relistar: () => this.listarEventos(),
    modo: 'trigger',
    filtro: FILTRO_EVENTOS_TECNICOS,
  });

  public loading = false;

  // Eventos y entidades extraídas
  public eventos: IEventoGenerico[] = [];
  public eventos$?: Subscription;
  public alarmas: IDispositivoAlarma[] = [];
  public luminarias: ILuminaria[] = [];

  // Tecnicos y vehiculos
  public tecnicos: IUsuario[] = [];
  public tecnicos$?: Subscription;
  public vehiculos: IReporteBase<'Tracker 4G'>[] = [];
  public vehiculos$?: Subscription;
  public vehiculosTecnicos: IReporteBase<'Tracker 4G'>[] = [];
  public vehiculosPendientes: IReporteBase<'Tracker 4G'>[] = [];
  public vehiculosPendientesCount = 0;

  // Tecnico seleccionado para filtrar el mapa
  public tecnicoSeleccionado: IUsuario | null = null;
  public tiposActivos = new Set<string>([
    'alarmas',
    'luminarias',
    'trackers',
    'sirenas',
    'vehiculos',
    'tecnicos',
  ]);

  // Sirenas
  public sirenasG: ISirenaG[] = [];
  public sirenasG$?: Subscription;
  public sirenas: ISirena[] = [];
  public sirenas$?: Subscription;

  @ViewChild(FlotanteServicioTecnicoComponent)
  private flotante?: FlotanteServicioTecnicoComponent;

  // Popup
  public infoPanel: IInfoPanel | null = null;
  private map: mapboxgl.Map | null = null;
  private currentPopup: mapboxgl.Popup | null = null;

  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseServicioTecnicoComponent';
  public tipoMapaBase: TipoMapaBase =
    localStorage.getItem(this.TIPO_MAPA_BASE_KEY) || 'osm';
  public mapasPersonalizados: ILayerMapaPersonalizado[] = [];
  public clientePropio: ICliente = LoginService.getClientePropio();

  // Se completa en ngOnInit según los módulos habilitados del usuario.
  public allowedTypes: string[] = [];

  private eventoPopulate: IPopulate[] = [
    {
      path: 'alarma',
      populate: [
        { path: 'domicilio', select: 'geojson direccion identificacion' },
        { path: 'cliente', select: 'nombre' },
        { path: 'modelo', select: 'modelo marca' },
      ],
    },
    {
      path: 'luminaria',
      select: 'ubicacion identificacion idCliente',
      populate: [{ path: 'cliente', select: 'nombre' }],
    },
  ];
  private eventoFilter: IFilter<IEventoGenerico> = {
    filtrador: 'Servicio Técnico',
    estado: { $ne: 'Finalizado' } as any,
    idEntidad: { $exists: true } as any,
  };
  private eventoQuery: IQueryParam = {
    filter: JSON.stringify(this.eventoFilter),
    populate: JSON.stringify(this.eventoPopulate),
    select: 'idEntidad estado titulo fechaCreacion tipoEvento detallesTecnicos',
    includeChildren: true,
    excludeTotalCount: true,
    unlimited: true,
  };

  private vehiculoPopulate: IPopulate[] = [
    {
      path: 'activo',
      select: 'identificacion categoria vehiculo funcion idCliente',
      populate: [
        { path: 'vehiculo.chofer', select: 'usuario datosPersonales' },
        { path: 'cliente', select: 'nombre' },
      ],
    },
  ];
  private vehiculoFilter: IFilter<IReporteGenerico> = {
    tipoEntidad: 'Vehiculo',
  };
  private vehiculoQuery: IQueryParam = {
    filter: JSON.stringify(this.vehiculoFilter),
    populate: JSON.stringify(this.vehiculoPopulate),
    includeChildren: true,
  };

  private tecnicoQuery: IQueryParam = {
    select: '_id usuario datosPersonales',
    unlimited: true,
  };

  private sirenaGPopulate: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
  ];
  private sirenaGQuery: IQueryParam = {
    select: '_id idExterno idCliente',
    populate: JSON.stringify(this.sirenaGPopulate),
    includeChildren: true,
    unlimited: true,
  };
  private sirenaQuery: IQueryParam = {
    select: 'chipId geojson',
    includeChildren: true,
  };

  private listarEventos(): void {
    this.eventos$?.unsubscribe();
    this.eventos$ = this.lista2Service
      .eventosGenericos(this.eventoQuery, 120)
      .subscribe((data) => {
        this.eventos = data.datos;

        const alarmasMap = new Map<string, IDispositivoAlarma>();
        for (const evento of data.datos) {
          const alarma = (evento as any).alarma as IDispositivoAlarma;
          if (alarma?._id && alarma.domicilio?.geojson != null) {
            alarmasMap.set(alarma._id, alarma);
          }
        }
        this.alarmas = Array.from(alarmasMap.values());

        const luminariasMap = new Map<string, ILuminaria>();
        for (const evento of data.datos) {
          const luminaria = (evento as any).luminaria as ILuminaria;
          if (luminaria?._id && luminaria.ubicacion != null) {
            luminariasMap.set(luminaria._id, luminaria);
          }
        }
        this.luminarias = Array.from(luminariasMap.values());

        this.updateMap();
      });
  }

  private listarTecnicos(): void {
    this.tecnicos$?.unsubscribe();
    this.tecnicos$ = this.lista2Service
      .tecnicos(this.tecnicoQuery, 120)
      .subscribe((data) => {
        this.tecnicos = data.datos;
        this.updateMap();
      });
  }

  private listarVehiculos(): void {
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2Service
      .ultimosReportesGenericos<'Tracker 4G'>(this.vehiculoQuery, 120)
      .subscribe((data) => {
        this.vehiculos = data.datos;
        this.updateMap();
      });
  }

  private listarSirenasG(): void {
    this.sirenasG$?.unsubscribe();
    this.sirenasG$ = this.lista2Service
      .sirenasG(this.sirenaGQuery, 0)
      .subscribe((data) => {
        this.sirenasG = data.datos;
        this.listarSirenas();
      });
  }

  private listarSirenas(): void {
    this.sirenas$?.unsubscribe();
    this.sirenas$ = this.lista2Service
      .sirenas(this.sirenaQuery, 120)
      .subscribe((data) => {
        this.sirenas = data.datos;
        this.updateMap();
      });
  }

  public onTecnicoSeleccionado(tecnico: IUsuario | null): void {
    this.tecnicoSeleccionado = tecnico;
    this.updateMap();
  }

  public onTiposActivosChange(tipos: Set<string>): void {
    this.tiposActivos = tipos;
    this.updateMap();
  }

  public cerrarPanel(): void {
    this.infoPanel = null;
    this.currentPopup?.remove();
    this.currentPopup = null;
  }

  public editarEvento(ev: IEventoGenerico): void {
    const data: ICrearEditarEventoTecnicoGenericoData = { id: ev._id };
    this.formularios
      .abrir(CrearEditarEventoTecnicoGenericoComponent, {
        tamano: 'grande',
        data,
      })
      .afterClosed()
      .subscribe(() => this.listarEventos());
  }

  public asignarEvento(ev: IEventoGenerico): void {
    const data = {
      id: ev._id,
      idTecnico: ev.detallesTecnicos?.idTecnicoAsignado,
    };
    this.formularios
      .abrir(AsignarGenericoComponent, {
        tamano: 'mediano',
        data,
      })
      .afterClosed()
      .subscribe(() => this.listarEventos());
  }

  public async atenderEvento(ev: IEventoGenerico): Promise<void> {
    if (!ev._id) return;
    this.dialog
      .open(TratamientoGenericoComponent, {
        data: { idEvento: ev._id },
        hasBackdrop: false,
        minWidth: '1100px',
        maxWidth: '1500px',
      })
      .afterClosed()
      .subscribe(() => this.listarEventos());
  }

  private nombreTecnico(idTecnico: string | undefined): string | undefined {
    if (!idTecnico) return undefined;
    const tecnico = this.tecnicos.find((t) => t._id === idTecnico);
    if (!tecnico) return undefined;
    const datos = (tecnico as any).datosPersonales;
    if (datos?.nombre) {
      return `${datos.nombre}${datos.apellido ? ' ' + datos.apellido : ''}`;
    }
    return tecnico.usuario;
  }

  private eventosParaEntidad(
    tipoEvento: string,
    idEntidad: string,
  ): (IEventoGenerico & { _tecnicoNombre?: string })[] {
    return this.eventos
      .filter(
        (e) =>
          (e as any).tipoEvento === tipoEvento && e.idEntidad === idEntidad,
      )
      .map((e) => ({
        ...e,
        _tecnicoNombre: this.nombreTecnico(
          (e as any).detallesTecnicos?.idTecnicoAsignado,
        ),
      }));
  }

  public onEntityClick(data: any): void {
    if (!data) {
      this.cerrarPanel();
      return;
    }

    // Ocultar el contenido actual antes de actualizar, para evitar que el
    // popup existente muestre datos del nuevo marker mientras Angular re-renderiza
    const existingEl = document.getElementById('st-popup-container');
    if (existingEl) existingEl.style.visibility = 'hidden';

    const { type, id, coordinates } = data;
    const lngLat: [number, number] = [coordinates.lng, coordinates.lat];

    if (type === 'alarmas') {
      const alarma = this.alarmas.find((a) => a._id === id);
      if (!alarma) return;
      const marca = (alarma as any).modelo?.marca || '';
      const modelo = (alarma as any).modelo?.modelo || '';
      this.infoPanel = {
        tipo: 'alarma',
        nombre: (alarma as any).domicilio?.identificacion || alarma._id || id,
        cliente: (alarma as any).cliente?.nombre,
        direccion: (alarma as any).domicilio?.direccion,
        marcaModelo: [marca, modelo].filter(Boolean).join(' ') || undefined,
        eventosTecnicos: this.eventosParaEntidad('Evento Técnico Alarma', id),
      };
    } else if (type === 'luminarias') {
      const luminaria = this.luminarias.find((l) => l._id === id);
      if (!luminaria) return;
      this.infoPanel = {
        tipo: 'luminaria',
        nombre: luminaria.identificacion || luminaria._id || id,
        cliente: (luminaria as any).cliente?.nombre,
        eventosTecnicos: this.eventosParaEntidad(
          'Evento Técnico Luminaria',
          id,
        ),
      };
    } else if (type === 'sirenas') {
      const sirenaG = this.sirenasG.find((sg) => sg.idExterno === id);
      this.infoPanel = {
        tipo: 'sirena',
        nombre: id,
        cliente: (sirenaG as any)?.cliente?.nombre,
        eventosTecnicos: sirenaG
          ? this.eventosParaEntidad('Evento Técnico Sirena', sirenaG._id!)
          : [],
      };
    } else if (type === 'vehiculos') {
      // Vehículo de técnico → seleccionar técnico en el buscador
      const vehiculoTecnico = this.vehiculosTecnicos.find((v) => v._id === id);
      if (vehiculoTecnico) {
        const idChofer = (vehiculoTecnico.activo as any)?.vehiculo?.idChofer;
        const tecnico = this.tecnicos.find((t) => t._id === idChofer);
        if (!tecnico) return;
        this.cerrarPanel();
        this.flotante?.seleccionarTecnico(tecnico);
        return;
      }
      // Vehículo con servicio pendiente → mostrar popup
      const vehiculoPendiente = this.vehiculosPendientes.find(
        (v) => v._id === id,
      );
      if (!vehiculoPendiente) return;
      const idActivo = vehiculoPendiente.activo?._id;
      const activo = vehiculoPendiente.activo as any;
      const patente = activo?.vehiculo?.patente;
      const categoria = activo?.categoria;
      this.infoPanel = {
        tipo: 'vehiculo',
        nombre: activo?.identificacion || id,
        cliente: activo?.cliente?.nombre,
        patente: patente || undefined,
        marcaModelo: categoria || undefined,
        eventosTecnicos: idActivo
          ? this.eventosParaEntidad('Evento Técnico Tracker', idActivo)
          : [],
      };
    } else {
      this.cerrarPanel();
      return;
    }

    // Forzar actualización sincrónica del DOM antes de adjuntar al mapa,
    // para evitar que el popup muestre contenido viejo mientras Angular re-renderiza
    this.cdr.detectChanges();
    this.adjuntarPopup(lngLat);
  }

  private adjuntarPopup(coordinate: [number, number], intentos = 0): void {
    if (!this.map) return;
    const element = document.getElementById('st-popup-container');
    if (!element) {
      if (intentos < 10) {
        setTimeout(() => this.adjuntarPopup(coordinate, intentos + 1), 50);
      }
      return;
    }
    this.currentPopup?.remove();
    this.currentPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: '340px',
      anchor: 'bottom',
      className: 'mapbox-popup-angular',
      offset: [0, -10],
    })
      .setDOMContent(element)
      .setLngLat(coordinate)
      .addTo(this.map);
    element.style.visibility = 'visible';
  }

  private updateMap() {
    if (!this.mapaFacilV2Service.isReady()) return;

    const tecnicoIds = new Set(this.tecnicos.map((t) => t._id));
    this.vehiculosTecnicos = this.vehiculos.filter((v) => {
      const idChofer = (v.activo as any)?.vehiculo?.idChofer;
      return idChofer && tecnicoIds.has(idChofer);
    });

    const eventosFiltrados = this.tecnicoSeleccionado
      ? this.eventos.filter(
          (e) =>
            (e as any).detallesTecnicos?.idTecnicoAsignado ===
            this.tecnicoSeleccionado!._id,
        )
      : this.eventos;

    const idsAlarmaConEvento = new Set(
      eventosFiltrados
        .filter((e) => (e as any).tipoEvento === 'Evento Técnico Alarma')
        .map((e) => e.idEntidad),
    );
    const alarmasFiltradas = this.alarmas.filter(
      (a) => a._id && idsAlarmaConEvento.has(a._id),
    );

    const idsLuminariaConEvento = new Set(
      eventosFiltrados
        .filter((e) => (e as any).tipoEvento === 'Evento Técnico Luminaria')
        .map((e) => e.idEntidad),
    );
    const luminariasParaMapa = this.luminarias.filter(
      (l) => l._id && idsLuminariaConEvento.has(l._id),
    );

    const vehiculosFiltrados = this.tecnicoSeleccionado
      ? this.vehiculosTecnicos.filter(
          (v) =>
            (v.activo as any)?.vehiculo?.idChofer ===
            this.tecnicoSeleccionado!._id,
        )
      : this.vehiculosTecnicos;

    // Vehículos con servicio técnico pendiente (Evento Técnico Tracker)
    const idsTrackerConEvento = new Set(
      eventosFiltrados
        .filter((e) => (e as any).tipoEvento === 'Evento Técnico Tracker')
        .map((e) => e.idEntidad),
    );
    const idsTecnicos = new Set(vehiculosFiltrados.map((v) => v._id));
    // Pendientes: vehículos con evento técnico tracker, sin duplicar los de técnico
    this.vehiculosPendientes = this.vehiculos.filter(
      (v) =>
        v.activo?._id &&
        idsTrackerConEvento.has(v.activo._id) &&
        !idsTecnicos.has(v._id),
    );
    this.vehiculosPendientesCount = this.vehiculosPendientes.length;

    // Técnicos: marcar con _esTecnico para usar el pin del mapa de vehículos
    const vehiculosTecnicosConFlag = vehiculosFiltrados.map((v) => ({
      ...v,
      _esTecnico: true,
    }));

    const tecnicosVisibles = this.tiposActivos.has('tecnicos')
      ? vehiculosTecnicosConFlag
      : [];
    const pendientesVisibles = this.tiposActivos.has('vehiculos')
      ? this.vehiculosPendientes
      : [];
    const todosVehiculos = [...tecnicosVisibles, ...pendientesVisibles];

    const idsSirenaGConEvento = new Set(
      eventosFiltrados
        .filter((e) => (e as any).tipoEvento === 'Evento Técnico Sirena')
        .map((e) => e.idEntidad),
    );
    const sirenasConEvento = this.sirenasG.filter(
      (sg) => sg._id && idsSirenaGConEvento.has(sg._id),
    );
    const sirenasPorIdExterno = new Map(this.sirenas.map((s) => [s._id, s]));
    const sirenasParaMapa = sirenasConEvento
      .map((sg) =>
        sg.idExterno ? sirenasPorIdExterno.get(sg.idExterno) : undefined,
      )
      .filter(
        (s): s is ISirena => s !== undefined && s.geojson?.coordinates != null,
      )
      .map((s) => ({
        _id: s._id,
        coordenadas: [s.geojson!.coordinates[0], s.geojson!.coordinates[1]] as [
          number,
          number,
        ],
        ...s,
      }));

    this.mapaFacilV2Service.updateData({
      alarmas: this.tiposActivos.has('alarmas') ? alarmasFiltradas : [],
      luminarias: this.tiposActivos.has('luminarias') ? luminariasParaMapa : [],
      vehiculos: todosVehiculos,
      sirenas: this.tiposActivos.has('sirenas') ? sirenasParaMapa : [],
    });
  }

  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;
    this.updateMap();
    this.aplicarIconosVehiculos(map);
    // Re-aplicar después de cambios de estilo (los layers se recrean)
    map.on('style.load', () => this.aplicarIconosVehiculos(map));
  }

  private async aplicarIconosVehiculos(
    map: mapboxgl.Map,
    intentos = 0,
  ): Promise<void> {
    // Mapa ya destruido (se salió de la pantalla): no hay nada que aplicar.
    if (this.map !== map) return;
    // Esperar a que el estilo y los layers estén (no lo están durante un cambio
    // de capa base). `isStyleLoaded` también da falso con el mapa removido.
    if (!map.isStyleLoaded() || !map.getLayer('vehiculos-layer')) {
      if (intentos < 10)
        setTimeout(() => this.aplicarIconosVehiculos(map, intentos + 1), 150);
      return;
    }
    try {
      const tecnicoKey =
        await this.vehicleCompositeIconService.ensureMergedIcon(map, {
          reporte: { activo: { vehiculo: { tipo: 'Auto' } } } as any,
          categoriaMarker: 'tipo',
          isSelected: false,
          ringColor: '#4CAF50', // Verde permanente para técnicos
          orientation: 0,
        });

      // Durante el await pudo cambiar la capa base (layers recreados, estilo
      // cargando) o destruirse el mapa: tocarlo ahora tiraba «Error generando
      // ícono compuesto de técnico». Si el estilo cambió, el listener de
      // 'style.load' vuelve a llamar a este método con el estilo nuevo.
      if (
        this.map !== map ||
        !map.isStyleLoaded() ||
        !map.getLayer('vehiculos-layer') ||
        !map.hasImage(tecnicoKey)
      ) {
        return;
      }
      const iconExpr: mapboxgl.Expression = [
        'case',
        ['boolean', ['get', 'esTecnico'], false],
        tecnicoKey,
        'vehiculo-icon',
      ];
      const sizeExpr: mapboxgl.Expression = [
        'case',
        ['boolean', ['get', 'esTecnico'], false],
        0.264,
        0.2,
      ];
      const bgFilter: mapboxgl.Expression = [
        '!',
        ['boolean', ['get', 'esTecnico'], false],
      ];

      // Layer principal (clustering OFF)
      map.setLayoutProperty('vehiculos-layer', 'icon-image', iconExpr);
      map.setLayoutProperty('vehiculos-layer', 'icon-size', sizeExpr);
      if (map.getLayer('vehiculos-orientation-layer')) {
        map.setFilter('vehiculos-orientation-layer', bgFilter);
      }
      // Layers unclustered (clustering ON)
      if (map.getLayer('vehiculos-unclustered-foreground-layer')) {
        map.setLayoutProperty(
          'vehiculos-unclustered-foreground-layer',
          'icon-image',
          iconExpr,
        );
        map.setLayoutProperty(
          'vehiculos-unclustered-foreground-layer',
          'icon-size',
          sizeExpr,
        );
      }
      if (map.getLayer('vehiculos-unclustered-background-layer')) {
        map.setFilter('vehiculos-unclustered-background-layer', [
          'all',
          ['!', ['has', 'point_count']],
          bgFilter,
        ]);
      }
    } catch (e) {
      console.error('Error generando ícono compuesto de técnico:', e);
    }
  }

  async ngOnInit() {
    this.loading = true;
    // Filtros del mapa según módulos habilitados del usuario.
    this.allowedTypes = this.construirAllowedTypes();
    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }
    this.reconciliacionWs.iniciar();
    this.listarEventos();
    this.listarTecnicos();
    if (this.allowedTypes.includes('vehiculos')) this.listarVehiculos();
    if (this.allowedTypes.includes('sirenas')) this.listarSirenasG();
    this.loading = false;
  }

  // Tipos de entidad visibles en el mapa/filtros según los módulos del usuario.
  private construirAllowedTypes(): string[] {
    const tipos: string[] = [];
    if (this.helper.tieneModulo('Alarmas')) tipos.push('alarmas');
    if (this.helper.tieneModulo('Luminarias')) tipos.push('luminarias');
    if (this.helper.tieneModulo('Vehículos')) tipos.push('vehiculos');
    if (this.helper.tieneModulo('Sirenas')) tipos.push('sirenas');
    return tipos;
  }

  ngOnDestroy() {
    this.reconciliacionWs.detener();
    this.eventos$?.unsubscribe();
    this.tecnicos$?.unsubscribe();
    this.vehiculos$?.unsubscribe();
    this.sirenasG$?.unsubscribe();
    this.sirenas$?.unsubscribe();
    this.currentPopup?.remove();
    // El servicio de íconos es root y guarda el mapa como key: sin esto cada
    // visita dejaba el mapa removido retenido en memoria.
    if (this.map) {
      try {
        this.vehicleCompositeIconService.cleanupMap(this.map);
      } catch {
        // El mapa ya pudo haberse removido; alcanza con soltar la referencia.
      }
      this.map = null;
    }
    this.mapaFacilV2Service.clearAllData();
  }
}
